import { alertApiRef, IdentityApi, identityApiRef, ProfileInfo, useApi } from '@backstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';

type BackstageIdentity = Awaited<ReturnType<IdentityApi['getBackstageIdentity']>>;

type UserProfileData = {
  profile: ProfileInfo;
  backstageIdentity: BackstageIdentity;
};

type UserProfileState = {
  profile: ProfileInfo;
  displayName: string;
  loading: boolean;
  backstageIdentity?: BackstageIdentity;
};

const loadingState: UserProfileState = {
  profile: {},
  displayName: '',
  loading: true,
};

const getCatalogPicture = (profile: unknown): string | undefined => {
  if (typeof profile === 'object' && profile !== null && 'picture' in profile && typeof profile.picture === 'string') {
    return profile.picture;
  }

  return undefined;
};

export class UserProfileSessionCache {
  private readonly requests = new WeakMap<IdentityApi, WeakMap<CatalogApi, Promise<UserProfileData>>>();
  private readonly resolved = new WeakMap<IdentityApi, WeakMap<CatalogApi, UserProfileData>>();

  /**
   * Returns already-resolved profile data synchronously, if present, without triggering a fetch
   * or awaiting a promise. Callers can use this to seed initial render state and skip a loading
   * state entirely when the data was already fetched earlier in the browser session.
   */
  peek(identityApi: IdentityApi, catalogApi: CatalogApi): UserProfileData | undefined {
    return this.resolved.get(identityApi)?.get(catalogApi);
  }

  get(identityApi: IdentityApi, catalogApi: CatalogApi): Promise<UserProfileData> {
    let requestsByCatalogApi = this.requests.get(identityApi);
    if (!requestsByCatalogApi) {
      requestsByCatalogApi = new WeakMap();
      this.requests.set(identityApi, requestsByCatalogApi);
    }

    const cachedRequest = requestsByCatalogApi.get(catalogApi);
    if (cachedRequest) {
      return cachedRequest;
    }

    const request = this.load(identityApi, catalogApi)
      .then(data => {
        this.setResolved(identityApi, catalogApi, data);
        return data;
      })
      .catch(error => {
        requestsByCatalogApi.delete(catalogApi);
        throw error;
      });
    requestsByCatalogApi.set(catalogApi, request);
    return request;
  }

  private setResolved(identityApi: IdentityApi, catalogApi: CatalogApi, data: UserProfileData): void {
    let resolvedByCatalogApi = this.resolved.get(identityApi);
    if (!resolvedByCatalogApi) {
      resolvedByCatalogApi = new WeakMap();
      this.resolved.set(identityApi, resolvedByCatalogApi);
    }
    resolvedByCatalogApi.set(catalogApi, data);
  }

  private async load(identityApi: IdentityApi, catalogApi: CatalogApi): Promise<UserProfileData> {
    // getProfileInfo and getBackstageIdentity are independent identityApi calls, so run them
    // concurrently rather than serially. getEntityByRef depends on the resolved
    // backstageIdentity.userEntityRef, so it must wait until identity resolves.
    const [identityProfile, backstageIdentity] = await Promise.all([
      identityApi.getProfileInfo(),
      identityApi.getBackstageIdentity(),
    ]);
    const catalogProfile = await catalogApi.getEntityByRef(backstageIdentity.userEntityRef);
    const catalogPicture = getCatalogPicture(catalogProfile?.spec?.profile);

    const profile =
      identityProfile.picture === undefined && catalogPicture
        ? { ...identityProfile, picture: catalogPicture }
        : identityProfile;

    return { profile, backstageIdentity };
  }
}

const toResolvedState = ({ profile, backstageIdentity }: UserProfileData): UserProfileState => ({
  profile,
  backstageIdentity,
  displayName: profile.displayName ?? backstageIdentity.userEntityRef,
  loading: false,
});

const browserSessionCache = new UserProfileSessionCache();
const UserProfileContext = createContext<UserProfileState | undefined>(undefined);

type UserProfileProviderProps = PropsWithChildren<{
  cache?: UserProfileSessionCache;
}>;

export const UserProfileProvider = ({ cache = browserSessionCache, children }: UserProfileProviderProps) => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  // Seed state synchronously from an already-resolved cache entry so a remount within the same
  // browser session (e.g. revisiting the settings page) renders the final profile immediately,
  // instead of flashing a loading state while the effect below re-reads the settled promise.
  const [state, setState] = useState<UserProfileState>(() => {
    const cached = cache.peek(identityApi, catalogApi);
    return cached ? toResolvedState(cached) : loadingState;
  });

  useEffect(() => {
    let mounted = true;

    cache
      .get(identityApi, catalogApi)
      .then(data => {
        if (mounted) {
          setState(toResolvedState(data));
        }
      })
      .catch(error => {
        if (mounted) {
          alertApi.post({
            message: `Failed to load user profile: ${error instanceof Error ? error.message : String(error)}`,
            severity: 'error',
          });
          setState({ ...loadingState, loading: false });
        }
      });

    return () => {
      mounted = false;
    };
  }, [alertApi, cache, catalogApi, identityApi]);

  return <UserProfileContext.Provider value={state}>{children}</UserProfileContext.Provider>;
};

export const useUserProfile = (): UserProfileState => {
  const profile = useContext(UserProfileContext);
  if (!profile) {
    throw new Error('useUserProfile must be used within UserProfileProvider');
  }

  return profile;
};
